import { Icon, InlineStack, Link, Paragraph, Stack } from '@customerio/pluma-components/react';
import { SegmentDetailsPopover } from '@/features/condition-viewer';
import { getDesignStudioTypeLabel } from '../utils/item';
import type { UsageRecord } from '../item/usage-links';

/**
 * One thing that uses the item, inside its surface's accordion.
 *
 * A segment and a campaign carry a description worth reading beside the
 * name — it is usually what says whether this is the one in the way. A
 * segment also opens onto the whole of itself on hover, archived or not, as
 * the page this replaces does (`data-index-details-drawer/usage-item`).
 */

type UsageItemProps = {
  workspaceId: number;
  record: UsageRecord;
  /** Where the record opens, or nothing when it carries no id to open. */
  href: string | null;
};

/** What a row says under its name, where it has something to say. */
function descriptionOf(record: UsageRecord): string | null {
  if ('segment_id' in record || 'campaign_id' in record) {
    return record.description === '' ? null : record.description;
  }
  if ('short_id' in record) {
    return getDesignStudioTypeLabel(record.type);
  }
  return null;
}

export function UsageItem({ workspaceId, record, href }: UsageItemProps) {
  const description = descriptionOf(record);

  return (
    <Stack gap="100">
      <InlineStack alignBlock="center" gap="100">
        {'segment_id' in record ? (
          <SegmentDetailsPopover segmentId={record.segment_id} workspaceId={workspaceId} triggerLabel="Usage detail">
            <Icon name="info" size="sm" color="subtle" display="block" />
          </SegmentDetailsPopover>
        ) : null}
        {href == null ? (
          <Paragraph>{record.name}</Paragraph>
        ) : (
          <Link href={href} icon="exit" iconPosition="trailing" variant="primary" isUnderlined={false} target="_blank">
            {record.name}
          </Link>
        )}
      </InlineStack>
      {description == null || description === '' ? null : <Paragraph variant="secondary">{description}</Paragraph>}
    </Stack>
  );
}
